import { API, API_BASE } from '../../config/api';
import { useState, useRef, useEffect, useCallback } from 'react';
import { Canvas as FabricCanvas, FabricImage } from 'fabric';
import { api } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Upload, Loader2, RotateCcw, ZoomIn, ZoomOut, Save, Eye, Shirt, Move, ImagePlus, Trash2 } from 'lucide-react';


// Product catalog — real product photos, Clifton Custom Clothing specs
const PRODUCT_CATALOG = [
  { 
    id: 'tshirt', label: 'T-Shirt', category: 'apparel',
    fabric: '100% preshrunk cotton, 5.3 oz/yd²', 
    sizes: ['S', 'M', 'L', 'XL', '2XL', '3XL'],
    designArea: { x: 0.25, y: 0.20, w: 0.50, h: 0.45 },
    photo: 'https://images.pexels.com/photos/8532616/pexels-photo-8532616.jpeg?auto=compress&cs=tinysrgb&w=500&h=500&fit=crop',
  },
  { 
    id: 'longsleeve', label: 'Long Sleeve Tee', category: 'apparel',
    fabric: '100% preshrunk cotton, 5.3 oz/yd²',
    sizes: ['S', 'M', 'L', 'XL', '2XL', '3XL'],
    designArea: { x: 0.25, y: 0.20, w: 0.50, h: 0.40 },
    photo: 'https://images.pexels.com/photos/8532611/pexels-photo-8532611.jpeg?auto=compress&cs=tinysrgb&w=500&h=500&fit=crop',
  },
  { 
    id: 'crewneck', label: 'Crewneck Sweatshirt', category: 'apparel',
    fabric: '80% cotton / 20% polyester, 280gsm fleece-lined',
    sizes: ['S', 'M', 'L', 'XL', '2XL', '3XL'],
    designArea: { x: 0.22, y: 0.18, w: 0.56, h: 0.44 },
    photo: 'https://images.unsplash.com/photo-1598795737563-07467e744bac?w=500&h=500&fit=crop',
  },
  { 
    id: 'hoodie', label: 'Pullover Hoodie', category: 'apparel',
    fabric: '80% cotton / 20% polyester, 320gsm heavyweight fleece',
    sizes: ['S', 'M', 'L', 'XL', '2XL', '3XL'],
    designArea: { x: 0.24, y: 0.26, w: 0.52, h: 0.38 },
    photo: 'https://images.unsplash.com/photo-1746971054333-8b9610b397a5?w=500&h=500&fit=crop',
  },
  { 
    id: 'tracksuit-jacket', label: 'Tracksuit Jacket (Zip-Up)', category: 'apparel',
    fabric: 'Tech polyester, reinforced stitching, full zip front',
    sizes: ['S', 'M', 'L', 'XL', '2XL'],
    designArea: { x: 0.22, y: 0.18, w: 0.56, h: 0.42 },
    photo: 'https://images.unsplash.com/photo-1499971856191-1a420a42b498?w=500&h=500&fit=crop',
  },
  { 
    id: 'tracksuit-pants', label: 'Tracksuit Pants', category: 'bottoms',
    fabric: 'Tech polyester, tapered leg, zippered pockets',
    sizes: ['S', 'M', 'L', 'XL', '2XL'],
    designArea: { x: 0.28, y: 0.10, w: 0.44, h: 0.32 },
    photo: 'https://images.unsplash.com/photo-1621573094640-0b2391e9acec?w=500&h=500&fit=crop',
  },
  { 
    id: 'sweatpants', label: 'Open Bottom Sweatpants', category: 'bottoms',
    fabric: '50% cotton / 50% polyester, 280gsm, open bottom hem',
    sizes: ['S', 'M', 'L', 'XL', '2XL'],
    designArea: { x: 0.28, y: 0.10, w: 0.44, h: 0.32 },
    photo: 'https://images.unsplash.com/photo-1603954698693-b0bcbceb5ad0?w=500&h=500&fit=crop',
  },
  { 
    id: 'joggers', label: 'Fleece Joggers', category: 'bottoms',
    fabric: '80% cotton / 20% polyester, 280gsm, elastic cuffs',
    sizes: ['S', 'M', 'L', 'XL', '2XL'],
    designArea: { x: 0.28, y: 0.10, w: 0.44, h: 0.32 },
    photo: 'https://images.unsplash.com/photo-1566392722006-b3ccec017bec?w=500&h=500&fit=crop',
  },
  { 
    id: 'shorts', label: 'Athletic Shorts', category: 'bottoms',
    fabric: '4-way stretch microfiber, 140gsm, optional lycra inner',
    sizes: ['S', 'M', 'L', 'XL', '2XL'],
    designArea: { x: 0.22, y: 0.08, w: 0.56, h: 0.40 },
    photo: 'https://images.unsplash.com/photo-1719473442915-016f3ebda79a?w=500&h=500&fit=crop',
  },
];

const BUILT_IN_TEMPLATES = PRODUCT_CATALOG;

export function MockupCreator({ onMockupSaved }) {
  const canvasRef = useRef(null);
  const fabricRef = useRef(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [designImage, setDesignImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');
  const [publishNow, setPublishNow] = useState(true);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [removingBg, setRemovingBg] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [pendingTemplateUrl, setPendingTemplateUrl] = useState('');
  const [showQuickDraw, setShowQuickDraw] = useState(false);
  const designInputRef = useRef(null);
  const customTemplateInputRef = useRef(null);

  // Initialize canvas
  useEffect(() => {
    if (!canvasRef.current) return;
    const fc = new FabricCanvas(canvasRef.current, {
      width: 500,
      height: 500,
      backgroundColor: '#111111',
      selection: true,
    });
    fabricRef.current = fc;
    return () => { fc.dispose(); };
  }, []);

  // Load saved templates
  useEffect(() => {
    api.get(`${API}/mockup-templates`).then(r => setSavedTemplates(r.data)).catch(() => {});
  }, []);

  // Load an image URL onto the canvas as background
  const loadImageOnCanvas = useCallback((imageUrl, designArea) => {
    const fc = fabricRef.current;
    if (!fc) return;
    setDesignImage(null);
    fc.clear();
    fc.backgroundColor = '#111111';

    const imgEl = new window.Image();
    imgEl.crossOrigin = 'anonymous';
    imgEl.onload = () => {
      const fabricImg = new FabricImage(imgEl);
      const nw = imgEl.naturalWidth;
      const nh = imgEl.naturalHeight;
      const scale = Math.max(500 / nw, 500 / nh);
      fabricImg.set({
        scaleX: scale, scaleY: scale,
        left: (500 - nw * scale) / 2,
        top: (500 - nh * scale) / 2,
        selectable: false, evented: false,
        hoverCursor: 'default', originX: 'left', originY: 'top',
      });
      fc.add(fabricImg);
      fc.sendObjectToBack(fabricImg);
      fc.requestRenderAll();
    };
    imgEl.src = imageUrl;
    setSelectedTemplate({ id: 'saved', label: 'Custom', designArea: designArea || { x: 0.20, y: 0.20, w: 0.60, h: 0.60 } });
  }, []);

  // Load built-in template onto canvas — uses real product photo
  const loadTemplate = useCallback((template) => {
    if (!template.photo) return;
    setSelectedTemplate(template);
    loadImageOnCanvas(template.photo, template.designArea);
  }, [loadImageOnCanvas]);

  // Upload product image → remove bg in browser → load on canvas
  const handleCustomTemplateUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (customTemplateInputRef.current) customTemplateInputRef.current.value = '';

    setRemovingBg(true);
    toast.info('Removing background... this may take a moment.');

    try {
      // Convert file to base64 and send to backend for BG removal
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
      });
      reader.readAsDataURL(file);
      const base64Data = await base64Promise;

      const { data: bgResult } = await api.post(`${API}/artwork/remove-bg`, {
        image_base64: base64Data,
      }, { timeout: 120000 });

      // Upload the processed image to server for storage
      const processedBlob = await fetch(`data:image/png;base64,${bgResult.image_base64}`).then(r => r.blob());
      const processedFile = new File([processedBlob], `nobg-${Date.now()}.png`, { type: 'image/png' });
      const fd = new FormData();
      fd.append('file', processedFile);
      const { data } = await api.post(`${API}/upload/processed-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      const fullUrl = `${API_BASE}${data.url}`;

      // Load the bg-removed image on canvas
      loadImageOnCanvas(fullUrl);

      // Offer to save as template
      setPendingTemplateUrl(fullUrl);
      setTemplateName(file.name.replace(/\.[^/.]+$/, ''));
      setShowSaveTemplate(true);

      toast.success('Background removed! You can now place your design.');
    } catch (err) {
      console.error('BG removal error:', err);
      toast.error('Background removal failed. Loading original image.');
      // Fall back: upload original and use it
      const fd = new FormData();
      fd.append('file', file);
      try {
        const { data } = await api.post(`${API}/upload/cover`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const fullUrl = `${API_BASE}${data.url}`;
        loadImageOnCanvas(fullUrl);
        setPendingTemplateUrl(fullUrl);
        setTemplateName(file.name.replace(/\.[^/.]+$/, ''));
        setShowSaveTemplate(true);
      } catch {
        // Last resort: use local data URL
        const reader = new FileReader();
        reader.onload = (ev) => loadImageOnCanvas(ev.target.result);
        reader.readAsDataURL(file);
      }
    } finally {
      setRemovingBg(false);
    }
  };

  // Save current image as reusable template
  const saveAsTemplate = async () => {
    if (!templateName || !pendingTemplateUrl) return;
    try {
      const { data } = await api.post(`${API}/mockup-templates`, {
        name: templateName,
        image_url: pendingTemplateUrl,
        category: 'apparel',
      });
      setSavedTemplates(prev => [...prev, data]);
      setShowSaveTemplate(false);
      setPendingTemplateUrl('');
      toast.success(`"${templateName}" saved as template!`);
    } catch {
      toast.error('Failed to save template');
    }
  };

  // Delete a saved template
  const deleteSavedTemplate = async (id) => {
    try {
      await api.delete(`${API}/mockup-templates/${id}`);
      setSavedTemplates(prev => prev.filter(t => t.id !== id));
      toast.success('Template deleted');
    } catch {
      toast.error('Failed to delete template');
    }
  };

  // Add design artwork to canvas
  const handleDesignUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file || !fabricRef.current || !selectedTemplate) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const fc = fabricRef.current;
      const da = selectedTemplate.designArea;
      const imgEl = new window.Image();
      imgEl.onload = () => {
        const fabricImg = new FabricImage(imgEl);
        const nw = imgEl.naturalWidth;
        const nh = imgEl.naturalHeight;
        const maxW = 500 * da.w;
        const maxH = 500 * da.h;
        const scale = Math.min(maxW / nw, maxH / nh);
        fabricImg.set({
          scaleX: scale, scaleY: scale,
          left: 500 * da.x + (maxW - nw * scale) / 2,
          top: 500 * da.y + (maxH - nh * scale) / 2,
          cornerColor: '#FF3B30', cornerSize: 10,
          transparentCorners: false, borderColor: '#FF3B30',
          hasControls: true, hasBorders: true,
          originX: 'left', originY: 'top',
        });
        fc.add(fabricImg);
        fc.setActiveObject(fabricImg);
        fc.requestRenderAll();
        setDesignImage(fabricImg);
        toast.success('Design added! Drag to position, corners to resize/rotate.');
      };
      imgEl.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    if (designInputRef.current) designInputRef.current.value = '';
  };

  const resetDesign = () => {
    if (!designImage || !selectedTemplate || !fabricRef.current) return;
    const da = selectedTemplate.designArea;
    const maxW = 500 * da.w;
    const maxH = 500 * da.h;
    const sw = designImage.getScaledWidth();
    const sh = designImage.getScaledHeight();
    const scale = Math.min(maxW / sw, maxH / sh);
    designImage.set({
      scaleX: designImage.scaleX * scale, scaleY: designImage.scaleY * scale,
      left: 500 * da.x + (maxW - sw * scale) / 2,
      top: 500 * da.y + (maxH - sh * scale) / 2, angle: 0,
    });
    fabricRef.current.requestRenderAll();
  };

  const scaleDesign = (factor) => {
    if (!designImage || !fabricRef.current) return;
    designImage.set({ scaleX: designImage.scaleX * factor, scaleY: designImage.scaleY * factor });
    fabricRef.current.requestRenderAll();
  };

  // Save mockup as merch
  const saveMockup = async (publish) => {
    if (!fabricRef.current || !title) {
      toast.error('Please enter a title');
      return;
    }
    setSaving(true);
    setPublishNow(publish);
    try {
      const dataUrl = fabricRef.current.toDataURL({ format: 'png', quality: 0.9 });
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const file = new File([blob], `mockup-${Date.now()}.png`, { type: 'image/png' });

      const fd = new FormData();
      fd.append('file', file);
      const { data: uploadData } = await api.post(`${API}/upload/cover`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imageUrl = `${API_BASE}${uploadData.url}`;

      const selectedCatalog = PRODUCT_CATALOG.find(c => c.id === selectedTemplate?.id);
      await api.post(`${API}/merch`, {
        title, description,
        price: parseFloat(price) || 0,
        image: imageUrl,
        category: selectedCatalog?.category || 'apparel',
        sizes: selectedCatalog?.sizes || ['S', 'M', 'L', 'XL', '2XL'],
        fabric_details: selectedCatalog?.fabric || '',
        in_stock: publish,
        purchasable: publish,
      });

      toast.success(publish ? 'Mockup created and published!' : 'Mockup saved as draft!');
      if (onMockupSaved) onMockupSaved();
      setTitle(''); setPrice(''); setDescription(''); setDesignImage(null);
    } catch (e) {
      { const detail = e.response?.data?.detail; toast.error(typeof detail === 'string' ? detail : 'Failed to save mockup'); }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="mockup-creator">
      {/* Template Selection */}
      <div className="bg-[#141414] border border-[#262626] p-6">
        <h3 className="font-cabinet text-lg font-bold uppercase tracking-tight mb-4">1. Choose Product Template</h3>

        {/* Upload new template button — hero action */}
        <div className="mb-5">
          <button
            onClick={() => customTemplateInputRef.current?.click()}
            disabled={removingBg}
            className="w-full py-4 border-2 border-dashed border-[#FF3B30]/50 hover:border-[#FF3B30] bg-[#FF3B30]/5 hover:bg-[#FF3B30]/10 transition-all flex items-center justify-center gap-3 text-[#FF3B30]"
            data-testid="upload-template-btn"
          >
            {removingBg ? (
              <><Loader2 className="w-5 h-5 animate-spin" /> Removing background...</>
            ) : (
              <><ImagePlus className="w-5 h-5" /> Upload Product Photo <span className="text-xs text-[#A3A3A3] ml-1">(auto-removes background)</span></>
            )}
          </button>
        </div>

        {/* Save-as-template prompt */}
        {showSaveTemplate && (
          <div className="mb-5 bg-[#0A0A0A] border border-[#DAA520]/40 p-4 flex items-center gap-3">
            <Save className="w-5 h-5 text-[#DAA520] flex-shrink-0" />
            <Input
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Template name (e.g. T-Shirt Front)"
              className="flex-1 bg-[#141414] border-[#262626] rounded-none text-sm"
              data-testid="template-name-input"
            />
            <Button onClick={saveAsTemplate} className="bg-[#DAA520] hover:bg-[#DAA520]/80 text-black rounded-none text-sm px-4" data-testid="save-template-btn">
              Save Template
            </Button>
            <button onClick={() => setShowSaveTemplate(false)} className="text-[#A3A3A3] hover:text-white text-sm">Skip</button>
          </div>
        )}

        {/* Saved templates grid */}
        {savedTemplates.length > 0 && (
          <div className="mb-4">
            <p className="text-sm text-[#A3A3A3] mb-2 uppercase tracking-wider">Your Saved Templates</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
              {savedTemplates.map(t => (
                <div key={t.id} className="relative group">
                  <button
                    onClick={() => loadImageOnCanvas(t.image_url, t.design_area)}
                    className={`w-full p-2 border-2 transition-all ${selectedTemplate?.selectedSavedId === t.id ? 'border-[#FF3B30] ring-1 ring-[#FF3B30]' : 'border-[#262626] hover:border-[#444]'}`}
                    data-testid={`saved-template-${t.id}`}
                  >
                    <div className="aspect-square bg-[#111] overflow-hidden flex items-center justify-center">
                      <img src={t.image_url} alt={t.name} className="w-full h-full object-contain" />
                    </div>
                    <p className="text-xs font-bold text-center mt-1 truncate">{t.name}</p>
                  </button>
                  <button
                    onClick={() => deleteSavedTemplate(t.id)}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white text-xs rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    data-testid={`delete-template-${t.id}`}
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick-draw built-in templates (collapsible) */}
        <div className="border-t border-[#262626] pt-3">
          <button
            onClick={() => setShowQuickDraw(!showQuickDraw)}
            className="text-sm text-[#A3A3A3] hover:text-white flex items-center gap-2 mb-2"
          >
            <Shirt className="w-4 h-4" />
            Quick-Draw Templates
            <span className="text-xs">({showQuickDraw ? 'hide' : 'show'})</span>
          </button>
          {showQuickDraw && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3">
              {BUILT_IN_TEMPLATES.map(t => (
                <button
                  key={t.id}
                  onClick={() => loadTemplate(t)}
                  className={`relative p-2 border-2 transition-all text-left ${selectedTemplate?.id === t.id ? 'border-[#FF3B30] ring-1 ring-[#FF3B30]' : 'border-[#262626] hover:border-[#444]'}`}
                  data-testid={`template-${t.id}`}
                >
                  <div className="aspect-square bg-[#111] mb-1 overflow-hidden flex items-center justify-center">
                    <img src={t.photo} alt={t.label} crossOrigin="anonymous" className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <p className="text-xs font-bold">{t.label}</p>
                  <p className="text-[9px] text-[#666] mt-0.5 leading-tight line-clamp-2">{t.fabric}</p>
                  <p className="text-[9px] text-[#555] mt-0.5">{t.sizes.join(' / ')}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Hidden file inputs */}
      <input type="file" ref={designInputRef} accept="image/*" className="hidden" onChange={handleDesignUpload} />
      <input type="file" ref={customTemplateInputRef} accept="image/*" className="hidden" onChange={handleCustomTemplateUpload} />

      {/* Canvas + Controls */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#141414] border border-[#262626] p-6">
          <h3 className="font-cabinet text-lg font-bold uppercase tracking-tight mb-4">2. Design Your Mockup</h3>
          <div className="mb-4 bg-[#0a0a0a] border border-[#262626] p-2 flex justify-center" style={{ overflow: 'hidden' }}>
            <div style={{ width: '500px', height: '500px' }}>
              <canvas ref={canvasRef} data-testid="mockup-canvas" />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={() => designInputRef.current?.click()} disabled={!selectedTemplate} variant="outline"
              className="rounded-none border-[#262626] hover:border-[#FF3B30]" data-testid="upload-design-btn">
              <Upload className="w-4 h-4 mr-2" /> Upload Artwork
            </Button>
            <Button onClick={resetDesign} disabled={!designImage} variant="outline" className="rounded-none border-[#262626] hover:border-[#FF3B30]" data-testid="reset-design-btn">
              <RotateCcw className="w-4 h-4 mr-2" /> Reset
            </Button>
            <Button onClick={() => scaleDesign(1.2)} disabled={!designImage} variant="outline" className="rounded-none border-[#262626] hover:border-[#FF3B30]">
              <ZoomIn className="w-4 h-4" />
            </Button>
            <Button onClick={() => scaleDesign(0.8)} disabled={!designImage} variant="outline" className="rounded-none border-[#262626] hover:border-[#FF3B30]">
              <ZoomOut className="w-4 h-4" />
            </Button>
          </div>
          {selectedTemplate && !designImage && (
            <p className="text-[#A3A3A3] text-sm mt-3 flex items-center gap-1"><Move className="w-4 h-4" /> Upload your artwork to place it on the product</p>
          )}
          {designImage && (
            <p className="text-[#A3A3A3] text-sm mt-3 flex items-center gap-1"><Move className="w-4 h-4" /> Drag to reposition. Use corners to resize/rotate.</p>
          )}
        </div>

        {/* Save Form */}
        <div className="bg-[#141414] border border-[#262626] p-6">
          <h3 className="font-cabinet text-lg font-bold uppercase tracking-tight mb-4">3. Save & Publish</h3>

          {/* Selected product info */}
          {selectedTemplate && PRODUCT_CATALOG.find(c => c.id === selectedTemplate.id) && (() => {
            const cat = PRODUCT_CATALOG.find(c => c.id === selectedTemplate.id);
            return (
              <div className="bg-[#0A0A0A] border border-[#262626] p-3 mb-4" style={{ borderRadius: 'var(--radius)' }}>
                <p className="text-[10px] text-[#DAA520] font-bold uppercase tracking-wider mb-1">Product Details</p>
                <p className="text-xs font-bold">{cat.label}</p>
                <p className="text-[10px] text-[#666] mt-1">{cat.fabric}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {cat.sizes.map(s => (
                    <span key={s} className="px-1.5 py-0.5 text-[9px] font-bold border border-[#333] text-[#888]">{s}</span>
                  ))}
                </div>
              </div>
            );
          })()}

          <div className="space-y-4">
            <div>
              <Label className="overline">Product Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none" placeholder="e.g. DugOwtBoi Signature Tee" data-testid="mockup-title" />
            </div>
            <div>
              <Label className="overline">Price ($)</Label>
              <Input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none" placeholder="29.99" data-testid="mockup-price" />
            </div>
            <div>
              <Label className="overline">Description</Label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="mt-2 w-full bg-[#0A0A0A] border border-[#262626] rounded-none p-3 text-white text-sm resize-none" rows={3} placeholder="Premium cotton, custom print..." data-testid="mockup-description" />
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={() => saveMockup(true)} disabled={saving || !title || !selectedTemplate}
                className="flex-1 bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-white rounded-none h-12 text-sm font-bold uppercase" data-testid="publish-now-btn">
                {saving && publishNow ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> PUBLISHING...</> : <><Eye className="w-4 h-4 mr-2" /> PUBLISH NOW</>}
              </Button>
              <Button onClick={() => saveMockup(false)} disabled={saving || !title || !selectedTemplate}
                variant="outline" className="flex-1 border-[#DAA520] text-[#DAA520] hover:bg-[#DAA520]/10 rounded-none h-12 text-sm font-bold uppercase" data-testid="save-draft-btn">
                {saving && !publishNow ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> SAVING...</> : <><Save className="w-4 h-4 mr-2" /> SAVE AS DRAFT</>}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
